<template>
  <div class="d-flex">
    <b-button
      class="d-flex align-items-center"
      variant="secondary-primary"
      @click="$bvModal.show('download_modal')"
    >
      <img :src="DownloadCloudIcon">
      <span class="ml-10-px">Download Shipment</span>
    </b-button>
    <b-dropdown
      v-if="isBrand"
      variant="secondary-black"
      text="Invoices"
      class="ml-1 split-action-dropdown"
      menu-class="shipment-dropdown-menu scroll-bar"
      toggle-class="shipment-toggle"
    >
      <template>
        <b-dropdown-item
          v-if="invoiceUrl"
          class="invoice-name"
        >
          <span
            @click="download(invoiceUrl)"
          >Download
            {{ getFileName(FILE_TYPES.INVOICE_BY_BRAND, invoiceUrl) }}</span>
        </b-dropdown-item>
        <b-dropdown-item
          @click="onClickUploadInvoice"
        >{{ isUpdateInvoiceEnabled ? 'Update' : 'Upload' }} Invoice</b-dropdown-item>
      </template>
    </b-dropdown>
    <b-modal
      id="invoice-modal"
      modal-class="custom-modal-variant custom-model"
      title="Upload invoice"
      centered
      no-close-on-backdrop
      no-close-on-esc
      size="lg"
      hide-footer
      @show="clearForm"
    >
      <template #modal-title>
        <div class="elements-center">
          <span class="modal-rounded-icon blue-border">
            <upload-icon />
          </span>
          <span> Upload invoice </span>
        </div>
      </template>
      <div class="invoice-upload-form">
        <validation-observer ref="validationObserver">
          <div
            @dragover.prevent
            @drop.prevent
          >
            <validation-provider
              #default="{ errors }"
              name="Invoice File"
              vid="Invoice file"
              rules="required"
            >
              <b-form-group label-for="uploadInvoice">
                <template #label>
                  <span>Upload Invoice
                    <span class="text-danger font-small-4">*</span></span>
                  <div
                    v-if="!invoiceFile"
                    class="fileDownload upload-invoice"
                    @drop="onDropInvoiceFile"
                  >
                    <upload-icon />
                    <h4 class="font-weight-bolder">
                      <b>Drag & Drop</b>
                    </h4>
                    <h6>or click to select files from your system</h6>
                  </div>
                </template>
                <div
                  v-if="invoiceFile"
                  class="file-uploaded mt-50"
                  @drop="onDropInvoiceFile"
                >
                  <div class="elements-center">
                    <b-img
                      :src="pdfIcon"
                      class="mx-1"
                      alt="ErrorIcon"
                    />
                    <div>
                      <div>Name: {{ invoiceFile.name }}</div>
                      <div>
                        Size:
                        {{ (formatNumber(invoiceFile.size / 1024), 2) }} KB
                      </div>
                    </div>
                  </div>
                  <div @click="invoiceFile = null">
                    <delete-icon />
                  </div>
                </div>
                <b-form-file
                  v-if="!invoiceFile"
                  id="uploadInvoice"
                  v-model="invoiceFile"
                  name="uploadInvoice"
                  :hidden="true"
                  plain
                />
                <span
                  v-if="errors.length"
                  class="text-danger font-small-4"
                >
                  {{ errors[0] }}
                </span>
              </b-form-group>
            </validation-provider>
          </div>
        </validation-observer>
        <div>
          <div class="d-flex">
            <div class="w-75 mr-1">
              <validation-provider
                #default="{ errors }"
                vid="invoice-amount"
                name="Invoice Amount"
                rules="required"
              >
                <b-form-group label-for="invoice-amount">
                  <template #label>
                    <span>Invoice Amount
                      <span class="text-danger font-small-4">*</span></span>
                  </template>
                  <b-form-input
                    id="invoice-amount"
                    v-model="invoiceAmount"
                    name="invoice-amount"
                    type="number"
                    placeholder="Type in the invoice amount"
                  />
                  <span
                    v-if="errors.length"
                    class="text-danger font-small-4"
                  >
                    {{ errors[0] }}
                  </span>
                </b-form-group>
              </validation-provider>
            </div>
            <div class="w-25">
              <b-form-group label-for="currency">
                <template #label>
                  <span>Currency
                    <span class="text-danger font-small-4">*</span></span>
                </template>
                <b-dropdown
                  class="w-100 currency-dropdown"
                  :text="currency"
                  variant="outline-none"
                  no-caret
                  toggle-class="border-link-water"
                >
                  <template #button-content>
                    {{ currency || 'Select' }}
                    <feather-icon
                      icon="ChevronDownIcon"
                      size="16"
                    />
                  </template>
                  <b-dropdown-item
                    v-for="currencyValue of Object.values(CURRENCY_CONVERSIONS).map(currency => currency.LABEL)"
                    :key="currencyValue"
                    :active="currency === currencyValue"
                    @click="currency = currencyValue"
                  >
                    {{ currencyValue }}
                  </b-dropdown-item>
                </b-dropdown>
              </b-form-group>
            </div>
          </div>
          <div class="d-flex">
            <div class="w-50 mr-1">
              <validation-provider
                #default="{ errors }"
                name="Invoice Quantity"
                vid="Quantity"
                rules="required"
              >
                <b-form-group label-for="invoice-quantity">
                  <template #label>
                    <span>Invoice Quantity
                      <span class="text-danger font-small-4">*</span></span>
                  </template>
                  <b-form-input
                    id="invoice-quantity"
                    v-model="invoiceQuantity"
                    name="invoice-quantity"
                    type="number"
                    placeholder="Type in the invoice quantity"
                  />
                  <span
                    v-if="errors.length"
                    class="text-danger font-small-4"
                  >
                    {{ errors[0] }}
                  </span>
                </b-form-group>
              </validation-provider>
            </div>
          </div>
          <div class="d-flex">
            <div class="w-100">
              <b-form-group label-for="invoice-amount-in-usd">
                <template #label>
                  <span>Invoice Amount in USD
                    <span class="text-danger font-small-4">*</span></span>
                </template>
                <b-form-input
                  id="invoice-amount-in-usd"
                  :value="getInvoiceAmountInUSDwithSymbol"
                  name="invoice-amount-in-usd"
                  disabled
                  placeholder="Invoice amount in USD"
                />
              </b-form-group>
            </div>
          </div>
        </div>
      </div>
      <div
        class="d-inline-block cursor-pointer mb-50"
        @click="isVisible = !isVisible"
      >
        <span class="color-dark-blue font-weight-bold">
          <collapse-icon
            :class="isVisible && 'down'"
            class="collapse-svg"
          />
          Expected Amount:</span>
        <span class="color-blue">
          {{ formatNumber(shipment.totalPriceWithVatForBrand || shipment.totalPriceForBrand, 2) }} USD
        </span>
      </div>
      <b-collapse
        id="expected"
        :visible="isVisible"
        class="amount-collapse-block"
      >
        <div class="d-flex justify-content-between">
          <span class="color-black">Total quantity:</span>
          <span class="color-actions-text-grey">{{ shipment.totalQuantity }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="color-black">Total cost:</span>
          <span class="color-actions-text-grey">{{
            formatNumber(shipment.totalPriceForBrand, 2)
          }}USD</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="color-black">Amount with VAT:</span>
          <span class="color-actions-text-grey">{{
            formatNumber(shipment.totalPriceWithVatForBrand || shipment.totalPriceForBrand, 2)
          }}USD</span>
        </div>
      </b-collapse>
      <div class="elements-center p-1">
        <b-button
          variant="outline-secondary-info mr-1"
          @click="$bvModal.hide('invoice-modal')"
        >
          Cancel
        </b-button>
        <b-button
          variant="secondary-info"
          :disabled="isUploading || !isValid"
          @click="uploadInvoice"
        >
          {{ isDispute ? isUploading ? 'Disputing' : 'Dispute' : isUploading ? 'Uploading' : 'Upload' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { toRefs } from '@vue/composition-api'
import {
  BDropdown,
  BDropdownItem,
  BModal,
  VBModal,
  BFormGroup,
  BFormInput,
  BFormFile,
  BImg,
  BCollapse,
  BButton,
} from 'bootstrap-vue'
import UploadIcon from '@/@core/assets/svg-components/UploadIcon.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import DeleteIcon from '@/@core/assets/svg-components/DeleteIcon.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import CollapseIcon from '@/@core/assets/svg-components/CollapseIcon.vue'
import DownloadCloudIcon from '@/assets/images/icons/download-cloud-icon.svg'
import { useShipmentAction } from '@/composables/useShipmentAction'

export default {
  name: 'ShipmentAction',
  components: {
    BDropdown,
    BDropdownItem,
    BModal,
    UploadIcon,
    ValidationObserver,
    ValidationProvider,
    BFormFile,
    BFormGroup,
    BFormInput,
    BImg,
    BCollapse,
    DeleteIcon,
    FeatherIcon,
    BButton,
    CollapseIcon,
},
  directives: {
    'b-modal': VBModal,
  },
  props: {
    shipment: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  emits: ['load-shipment'],
  setup(props) {
    const { shipment } = toRefs(props)
    const shipmentAction = useShipmentAction(shipment)
    shipmentAction.initInvoiceUrl()
    return {
      ...shipmentAction,
      doUploadInvoice: shipmentAction.uploadInvoice,
      pdfIcon: require('@/assets/images/icons/pdficon.svg'),
      DownloadCloudIcon,
    }
  },
  methods: {
    onClickUploadInvoice() {
      this.$bvModal.show('invoice-modal')
      if (this.isUpdateInvoiceEnabled) {
        this.setEditableFields()
      }
    },
    async uploadInvoice() {
      const result = await this.doUploadInvoice()
      if (result?.success) {
        this.$bvModal.hide('invoice-modal')
        this.$emit('load-shipment')
      }
    },
  },
}
</script>
